"use client";

import React from "react";
import { useController, Control, FieldPath, FieldValues } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Checkbox } from "../ui/checkbox";
import { cn } from "../../../lib/src/utils";

interface CheckboxInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  name: TName;
  control: Control<TFieldValues>;
  label: string;
  description?: string;
  helperText?: string;
  errorText?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}

export function CheckboxInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  name,
  control,
  label,
  description,
  helperText,
  errorText,
  className,
  required = false,
  disabled = false,
}: CheckboxInputProps<TFieldValues, TName>) {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });

  const hasError = error || errorText;

  return (
    <FormItem className={cn("space-y-2", className)}>
      <div className="flex items-center space-x-2">
        <FormControl>
          <Checkbox
            checked={field.value}
            onCheckedChange={field.onChange}
            disabled={disabled}
            className={cn(
              hasError && "border-destructive",
              disabled && "opacity-50 cursor-not-allowed"
            )}
          />
        </FormControl>
        <FormLabel className={cn(
          "text-sm font-medium leading-none cursor-pointer",
          required && "after:content-['*'] after:ml-0.5 after:text-red-500",
          disabled && "text-muted-foreground cursor-not-allowed"
        )}>
          {label}
        </FormLabel>
      </div>
      {description && <FormDescription className="text-sm text-muted-foreground">{description}</FormDescription>}
      {helperText && !hasError && <FormDescription className="text-sm text-muted-foreground">{helperText}</FormDescription>}
      {hasError && <FormMessage>{errorText || error?.message}</FormMessage>}
    </FormItem>
  );
}

